import { CalendarClock, Check, CircleCheck, CreditCard, Trash2, User, UserX, X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from '../../../lib/i18n';
import {
  calendarBookingActionTarget,
  heldBookingOps,
  type AwaitedBookingOutcome,
  type BookingOp,
  type CalendarBookingActionTarget,
} from '../../../lib/calendar/calendar-bookings';
import { awaitBookingOutcome, useAwaitedBookingOutcome } from '../../../lib/calendar/use-awaited-booking-outcome';
import type { CalendarItem } from '../../../lib/calendar/calendar-item';
import { fromDayParam, toDayParam } from '../../../lib/calendar/calendar-dates';
import { useBookingSlots } from '../../../lib/calendar/use-booking-card-reads';
import {
  cardActionBiEvent,
  executeCardAction,
  OUTCOME_UNKNOWN,
  prepareCalendarAction,
  TOKEN_INVALID,
  undoPrepareCardAction,
  type CalendarActionRequest,
  type CardActionStage,
  type PreparedCardAction,
} from '../../../lib/card-actions';
import { useAgentName } from '../../../lib/use-agent-name';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { Button, Card, Input, Switch, Toast } from '../ui';
import styles from './Calendar.module.css';

// A booking's own changes that need no conversation (approve, decline, check in, no-show, mark paid, reschedule,
// cancel), each prepared through the Calendar's checked booking write and confirmed right here with the harness's own
// reading of the live booking. The Calendar's details card and a booking's card in the chat draw the same buttons.

/** What a booking action did, for the toast shown once it is done. */
export interface CalendarActionNotice {
  message: string;
  severity: 'success' | 'error';
  undoToken?: string;
  op?: BookingOp;
  /** The booking's site and client the action changed, so an Undo can wait for the read that shows it reversed. */
  siteId?: string;
  record?: string;
}

/**
 * The booking's buttons, or the confirm or the new-time picker one of them opened. `idle` is drawn after the buttons
 * while neither is open.
 */
export function BookingActions({
  item,
  timeZone,
  time,
  onActed,
  idle,
}: {
  item: CalendarItem;
  timeZone: string;
  time: (at: number) => string;
  onActed: (notice: CalendarActionNotice) => void;
  idle?: ReactNode;
}) {
  const { t } = useTranslation();
  const agentName = useAgentName();
  const actions = useBookingActions(item, onActed);
  if (actions.confirm) {
    return (
      <BookingConfirm
        key={actions.confirm.prepared.token}
        prepared={actions.confirm.prepared}
        op={actions.confirm.op}
        initialNotify={actions.confirm.notify}
        initialMessage={actions.confirm.message}
        onAnswer={actions.answer}
      />
    );
  }
  if (actions.rescheduling !== undefined) {
    return (
      <Reschedule
        item={item}
        timeZone={timeZone}
        time={time}
        busy={!!actions.preparing}
        failed={actions.failed}
        onCancel={actions.stopRescheduling}
        onPick={(start) => void actions.prepare('bookings.reschedule', new Date(start).toISOString())}
      />
    );
  }
  return (
    <>
      {actions.buttons.length > 0 && (
        <span className={styles.detailsActions}>
          {actions.buttons.map(({ op, label, icon: Icon, destructive }) => (
            <Button
              key={op}
              // Approving is what a pending booking waits on, so it is the one primary action.
              variant={op === 'bookings.approve' ? 'contained' : 'outlined'}
              size="small"
              color={destructive ? 'destructive' : 'standard'}
              startIcon={<Icon size={16} aria-hidden />}
              loading={actions.preparing === op}
              disabled={!!actions.preparing || actions.executing}
              onClick={() => (op === 'bookings.reschedule' ? actions.startRescheduling() : void actions.prepare(op))}
            >
              {t(label)}
            </Button>
          ))}
        </span>
      )}
      {actions.failed && (
        <span role="alert" className={`sym-p3 ${styles.detailsError}`}>
          {t('calendar.details.refused', { maestroAgentName: agentName.name })}
        </span>
      )}
      {idle}
    </>
  );
}

/** The buttons the booking offers now: those its state allows, less those an action still waits to see read. */
function bookingButtons(item: CalendarItem, target: CalendarBookingActionTarget | undefined, outcome: AwaitedBookingOutcome | undefined) {
  if (!target) return [];
  const held = outcome ? heldBookingOps(outcome) : [];
  // Reschedule offers the service's open times for the booking's staff member, so it needs to know both.
  return BOOKING_BUTTONS.filter(
    (button) =>
      target[button.allowed] === true &&
      !held.includes(button.op) &&
      (button.op !== 'bookings.reschedule' || (!!item.ref.serviceId && !!item.staffResourceIds?.[0])),
  );
}

/** Whether the booking offers any button now: the chat card shows its Next steps only then. */
export function hasBookingActions(item: CalendarItem, now: number, outcome?: AwaitedBookingOutcome): boolean {
  return bookingButtons(item, calendarBookingActionTarget(item, now), outcome).length > 0;
}

/** The booking's own buttons, in the order the design draws them; which ones show is the booking's own state. */
const BOOKING_BUTTONS: { op: BookingOp; label: string; icon: typeof User; destructive?: boolean; allowed: keyof CalendarBookingActionTarget }[] = [
  { op: 'bookings.approve', label: 'goals.decision.approve', icon: Check, allowed: 'canApprove' },
  { op: 'bookings.decline', label: 'goals.decision.decline', icon: X, destructive: true, allowed: 'canDecline' },
  { op: 'bookings.checkIn', label: 'calendar.booking.checkIn', icon: CircleCheck, allowed: 'canCheckIn' },
  { op: 'bookings.markNoShow', label: 'calendar.booking.noShow', icon: UserX, allowed: 'canMarkNoShow' },
  { op: 'bookings.markPaid', label: 'calendar.booking.markPaid', icon: CreditCard, allowed: 'canMarkPaid' },
  { op: 'bookings.reschedule', label: 'calendar.booking.reschedule', icon: CalendarClock, allowed: 'canReschedule' },
  { op: 'bookings.cancel', label: 'common.cancel', icon: Trash2, destructive: true, allowed: 'canCancel' },
];

export function useBookingActions(item: CalendarItem, onActed: (notice: CalendarActionNotice) => void) {
  const { t } = useTranslation();
  const agentName = useAgentName();
  const { logItemSeen, logClickCta } = useGenericBi();
  const [preparing, setPreparing] = useState<BookingOp>();
  const [confirm, setConfirm] = useState<{
    prepared: PreparedCardAction;
    op: BookingOp;
    request: CalendarActionRequest;
    /** What the owner had chosen, kept when an expired reading is shown again. */
    notify?: boolean;
    message?: string;
  }>();
  const [rescheduling, setRescheduling] = useState<true>();
  const [failed, setFailed] = useState(false);
  const [executing, setExecuting] = useState(false);
  // Check in and No-show turn on when the booking starts or ends, so the card follows the clock while it is open.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const target = calendarBookingActionTarget(item, now);
  const awaitedOutcome = useAwaitedBookingOutcome(item, item.ref.siteId, item.ref.bookingId, now);
  const log = (stage: CardActionStage, op: BookingOp, outcome: string, durationMs?: number) => {
    const event = cardActionBiEvent(stage, op, outcome, durationMs);
    (event.type === 'itemSeen' ? logItemSeen : logClickCta)(event.componentName, { additionalFields: event.additionalFields });
  };

  const prepare = async (op: BookingOp, start?: string) => {
    if (!target) return;
    setFailed(false);
    setPreparing(op);
    const startedAt = Date.now();
    const request: CalendarActionRequest =
      op === 'bookings.reschedule'
        ? { siteId: target.siteId, record: target.record, op, reschedule: { start: start ?? '' } }
        : { siteId: target.siteId, record: target.record, op };
    const prepared = await prepareCalendarAction(request);
    setPreparing(undefined);
    if (prepared.refused) {
      log('refused', op, prepared.reason, Date.now() - startedAt);
      setFailed(true);
      return;
    }
    log('shown', op, 'do', Date.now() - startedAt);
    setRescheduling(undefined);
    setConfirm({ prepared, op, request });
  };

  const answer = async (confirmed: boolean, notifyCustomer: boolean, message: string) => {
    const current = confirm;
    setConfirm(undefined);
    if (!current) return;
    if (!confirmed) {
      log('cancelled', current.op, 'do');
      return;
    }
    log('confirmed', current.op, 'do');
    const startedAt = Date.now();
    setExecuting(true);
    const note = notifyCustomer && current.prepared.offersCustomerNotification ? message.trim() : '';
    const refused = (reason: string) => {
      setExecuting(false);
      log('refused', current.op, reason, Date.now() - startedAt);
      onActed({
        message: reason === OUTCOME_UNKNOWN ? t('cardAction.outcomeUnknown') : t('calendar.details.refused', { maestroAgentName: agentName.name }),
        severity: 'error',
      });
    };
    const done = await executeCardAction(current.prepared.token, { skipCustomerNotification: !notifyCustomer, ...(note ? { note } : {}) });
    // A confirm left open past the harness's hold on its reading comes back unread: the owner is shown the booking as it
    // is now and confirms again, since what runs must be what they saw.
    if (!done.ok && done.reason === TOKEN_INVALID) {
      const again = await prepareCalendarAction(current.request);
      setExecuting(false);
      if (again.refused) return refused(again.reason);
      log('shown', current.op, 'again', Date.now() - startedAt);
      setConfirm({ prepared: again, op: current.op, request: current.request, notify: notifyCustomer, message });
      return;
    }
    setExecuting(false);
    if (!done.ok) return refused(done.reason);
    log('done', current.op, 'ok', Date.now() - startedAt);
    // A read can land before the write shows in it; until one shows it, this action's buttons stay hidden.
    awaitBookingOutcome(current.request.siteId, current.request.record, {
      op: current.op,
      ...(current.request.op === 'bookings.reschedule' ? { rescheduledTo: Date.parse(current.request.reschedule.start) } : {}),
    });
    onActed({
      message: t('cardAction.done'),
      severity: 'success',
      op: current.op,
      siteId: current.request.siteId,
      record: current.request.record,
      ...(done.undoToken ? { undoToken: done.undoToken } : {}),
    });
  };

  return {
    buttons: bookingButtons(item, target, awaitedOutcome),
    preparing,
    executing,
    confirm,
    rescheduling,
    failed,
    prepare,
    answer,
    startRescheduling: () => {
      setFailed(false);
      setRescheduling(true);
    },
    stopRescheduling: () => setRescheduling(undefined),
  };
}

/** Wix's own limit on an owner's message inside its notification, as the harness checks it. */
const CLIENT_MESSAGE_MAX = 2000;

const CONFIRM_LABEL: Partial<Record<BookingOp, string>> = { 'bookings.cancel': 'calendar.details.cancelBooking' };

/**
 * The harness's reading of the live booking (its title and lines are what will happen), asked right on the card. The
 * client is told by default only where Wix would tell them anyway, which the switch can turn off.
 */
export function BookingConfirm({
  prepared,
  op,
  initialNotify = true,
  initialMessage = '',
  onAnswer,
}: {
  prepared: PreparedCardAction;
  op: BookingOp;
  initialNotify?: boolean;
  initialMessage?: string;
  onAnswer: (confirmed: boolean, notify: boolean, message: string) => void;
}) {
  const { t } = useTranslation();
  const [notify, setNotify] = useState(initialNotify);
  const [message, setMessage] = useState(initialMessage);
  const notes = [notify || !prepared.offersCustomerNotification ? prepared.customerNotice : '', !prepared.reversible ? t('cardAction.cannotUndo') : ''].filter(Boolean);
  return (
    <div className={styles.detailsConfirm}>
      <Card variant="stone" size="sm" className={styles.detailsConfirmBox} role="alert">
        <span className={`sym-type-small-medium ${prepared.destructive ? styles.detailsError : ''}`}>{prepared.title}</span>
        {[...prepared.lines, ...notes].map((line, index) => (
          <span key={`${index}:${line}`} dir="auto" className="sym-p3">
            {line}
          </span>
        ))}
      </Card>
      {prepared.offersCustomerNotification && <Switch checked={notify} onChange={setNotify} label={t('cardAction.notifyCustomer')} />}
      {prepared.offersCustomerNotification && notify && (
        <Input
          label={t('calendar.details.clientMessage')}
          value={message}
          maxLength={CLIENT_MESSAGE_MAX}
          onChange={(event) => setMessage(event.target.value)}
        />
      )}
      <span className={styles.detailsConfirmButtons}>
        <Button variant="outlined" size="small" onClick={() => onAnswer(false, notify, message)}>
          {t('calendar.details.keepIt')}
        </Button>
        <Button
          variant={prepared.destructive ? 'outlined' : 'contained'}
          color={prepared.destructive ? 'destructive' : 'standard'}
          size="small"
          onClick={() => onAnswer(true, notify, message)}
        >
          {t(CONFIRM_LABEL[op] ?? 'common.confirm')}
        </Button>
      </span>
    </div>
  );
}

/** The booking's own service and staff member's open times on a day, as Wix moves a booking only to one of them. */
function Reschedule({
  item,
  timeZone,
  time,
  busy,
  failed,
  onCancel,
  onPick,
}: {
  item: CalendarItem;
  timeZone: string;
  time: (at: number) => string;
  busy: boolean;
  failed: boolean;
  onCancel: () => void;
  onPick: (start: number) => void;
}) {
  const { t } = useTranslation();
  const agentName = useAgentName();
  const [day, setDay] = useState(() => toDayParam(item.start, timeZone));
  const slots = useBookingSlots({ siteId: item.ref.siteId, serviceId: item.ref.serviceId, staffResourceId: item.staffResourceIds?.[0], day, timeZone });
  const open = (slots.data ?? []).filter((slot) => slot.start !== item.start);
  return (
    <div className={styles.detailsConfirm}>
      <Input
        type="date"
        label={t('calendar.booking.newTime')}
        value={day}
        onChange={(event) => {
          if (fromDayParam(event.target.value, timeZone) !== undefined) setDay(event.target.value);
        }}
      />
      {slots.isInitialLoading ? (
        <span className={`sym-p3 ${styles.detailsSecondary}`}>{t('common.loading')}</span>
      ) : slots.isError ? (
        <span role="alert" className={`sym-p3 ${styles.detailsError}`}>
          {t('cardAction.failed')}
        </span>
      ) : open.length === 0 ? (
        <span className={`sym-p3 ${styles.detailsSecondary}`}>{t('calendar.book.noSlots')}</span>
      ) : (
        <span className={styles.detailsActions} role="group" aria-label={t('calendar.booking.newTime')}>
          {open.map((slot) => (
            <Button key={slot.start} variant="outlined" size="small" disabled={busy} onClick={() => onPick(slot.start)}>
              {time(slot.start)}
            </Button>
          ))}
        </span>
      )}
      {failed && (
        <span role="alert" className={`sym-p3 ${styles.detailsError}`}>
          {t('calendar.details.refused', { maestroAgentName: agentName.name })}
        </span>
      )}
      <span className={styles.detailsConfirmButtons}>
        <Button variant="outlined" size="small" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </span>
    </div>
  );
}

/**
 * The toast a booking action leaves once the card has closed, with Undo where the harness can reverse it (a check-in,
 * a no-show). The undo runs at once: the owner just watched the change it reverses.
 */
export function CalendarActionToast({ notice, onClose, onChanged }: { notice: CalendarActionNotice | undefined; onClose: () => void; onChanged: (notice: CalendarActionNotice) => void }) {
  const { t } = useTranslation();
  const { logItemSeen, logClickCta } = useGenericBi();
  const pressed = useRef<string | undefined>(undefined);
  const undo = async () => {
    const token = notice?.undoToken;
    const op = notice?.op ?? 'bookings.checkIn';
    // The toast's action can fire again before the notice clears, which would undo twice.
    if (!token || pressed.current === token) return;
    pressed.current = token;
    const log = (stage: CardActionStage, outcome: string) => {
      const event = cardActionBiEvent(stage, op, outcome);
      (event.type === 'itemSeen' ? logItemSeen : logClickCta)(event.componentName, { additionalFields: event.additionalFields });
    };
    log('confirmed', 'undo');
    const prepared = await undoPrepareCardAction(token);
    const done = prepared.refused ? undefined : await executeCardAction(prepared.token);
    if (!done?.ok) {
      log('refused', done?.reason ?? prepared.reason);
      onChanged({ message: t('cardAction.failed'), severity: 'error' });
      return;
    }
    log('undone', 'ok');
    if (notice?.siteId && notice.record) awaitBookingOutcome(notice.siteId, notice.record, { op, undone: true });
    onChanged({ message: t('cardAction.undone'), severity: 'success' });
  };
  return (
    <Toast
      open={!!notice}
      message={notice?.message ?? ''}
      severity={notice?.severity}
      action={notice?.undoToken ? { label: t('cardAction.undo'), onClick: () => void undo() } : undefined}
      onClose={onClose}
    />
  );
}
